import React from 'react';
import { withRouter, Link } from 'react-router-dom';
import { Container, Form } from 'react-bootstrap';
import Select from 'react-select/creatable';
import Creatable from 'react-select/creatable';

// Services & Helpers
import GlobalStateService from 'services/GlobalStateService';
import OnboardingService from 'services/OnboardingService';
import LanguageService from 'services/LanguageService';
import CourseService from 'services/CourseService';
import LocationService from 'services/LocationService';
import BootboxHelper from 'helpers/BootboxHelper';
import PageHeader from 'components/common/PageHeader';

// Components
import Loader from 'components/common/Loader';

//-------------------------------------------------------------------------------------------------------------------

class OnboardingPage extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            isLoading: true,
            isEditMode: false,
            isUpdating: false,
            page: 1
        };
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        this.load();
    }

    async load() {
        const loginInfo = GlobalStateService.getValue('loginInfo');
        this.setState({
            isLoading: true,
            isLoadingCities: true
        });
        let onboarding = await OnboardingService.get();        
        if (!onboarding) {
            onboarding = {
                instructor: loginInfo.instructor
            };
        };
        const languages = await LanguageService.list();
        const courses = await CourseService.list();
        const countries = await LocationService.listCountries();
        this.setState({
            onboarding,
            languages,
            courses,
            countries,
            isLoading: false
        }, () => {
            this.loadCities();
        });
    }

    async updateField(fieldName, value) {
        const onboarding = { ...this.state.onboarding };
        switch (fieldName) {
            case 'languages':
            case 'countryID':
            case 'cityID':
            case 'firstName':
            case 'lastName':
            case 'address':
            case 'email':
            case 'mobileNumber':
            case 'isExistingAAI':
            case 'courses':
                if (onboarding.instructor[fieldName] == value) {
                    return;
                }
                onboarding.instructor = { ...onboarding.instructor };
                onboarding.instructor[fieldName] = value;
                break;
            default:
                if (onboarding[fieldName] == value) {
                    return;
                }
                onboarding[fieldName] = value;
                break;
        }
        this.setState({
            onboarding
        }, () => {
            if (fieldName == 'countryID') {
                this.loadCities();
            }
        });
        this.setState({ isUpdating: true });
        if (fieldName == 'languages' || fieldName == 'courses') {
            value = value.map(v => v.id || v.courseID).join(',');
        }
        await OnboardingService.update(fieldName, value);
        this.setState({ isUpdating: false });
    }

    async loadCities() {
        const {
            countryID
        } = this.state.onboarding.instructor;
        this.setState({ isLoadingCities: true });
        let cities;
        if (countryID) {
            cities = await LocationService.listCities({
                countryID
            });
        } else {
            cities = [];
        }
        this.setState({
            isLoadingCities: false,
            cities
        });
    }

    prev() {
        const { page } = this.state;
        this.setState({
            page: page - 1
        }, () => {
            window.scrollTo(0, 0);
        });
    }

    async nextOrSubmit() {
        const { page } = this.state;
        if (page == 3) {
            this.setState({ isSubmitting: true });
            try {
                await OnboardingService.submit(this.state.onboarding.newPassword);
                this.updateField('dateSubmitted', new Date());
            } catch (error) {
                BootboxHelper.alert(error);
            } finally {
                this.setState({
                    isSubmitting: false
                }, () => {
                    window.scrollTo(0, 0);
                });
            }
        } else {
            this.setState({
                page: page + 1
            }, () => {
                window.scrollTo(0, 0);
            });
        }
    }

    //-------------------------------------------------------------------------------------------------------------------
    //  Render
    //-------------------------------------------------------------------------------------------------------------------

    render() {
        const {
            isLoading
        } = this.state;

        if (isLoading) {
            return (<Loader />);
        }

        return (<>

            <div className="page onboarding-page">

                <PageHeader>
                    <h1>Welcome to the Go Courses AAI Application</h1>
                    <h2>Please complete all fields which should only take a few minutes</h2>
                </PageHeader>
                
                <main>

                    <Container>
                        
                        {this.renderInner()}

                    </Container>

                </main>

            </div>

        </>);
    }

    renderInner() {
        const {
            onboarding,
            isUpdating,
            isSubmitting,
            page
        } = this.state;
        const {
            isAwaitingApproval
        } = this.props;

        // Awaiting approval
        if (isAwaitingApproval || onboarding.dateSubmitted) {
            return (
                <section className="submitted-section">
                    <h2>Thank you, your questionnaire has been submitted.</h2>
                    <p>
                        We will be in touch soon to discuss your application.
                    </p>
                </section>
            );
        }

        // Determine next button content
        let nextButtonContent;
        if (isSubmitting) {
            nextButtonContent = (<><Loader isInline /> Submitting...</>);
        } else if (page == 3) {
            nextButtonContent = (<>Submit questionnaire</>);
        } else {
            nextButtonContent = (<>Next page</>);
        }

        return (<>

            <section className="main-section">

                <div className="page-indicator-container">

                    <ol className="page-indicator">
                        <li className={page == 1 ? 'active' : ''}>1</li>
                        <li className={page == 2 ? 'active' : ''}>2</li>
                        <li className={page == 3 ? 'active' : ''}>3</li>
                    </ol>

                </div>

                {page == 1 && this.renderPage1()}
                {page == 2 && this.renderPage2()}
                {page == 3 && this.renderPage3()}
                
            </section>

            <section className="submit-section">

                {page > 1 &&
                    <button className="btn btn-secondary me-auto" onClick={() => this.prev()}>
                        Previous page
                    </button>
                }

                <button className="btn btn-primary ms-auto" onClick={() => this.nextOrSubmit()}>
                    {nextButtonContent}
                </button>

            </section>

        </>);
    }

    renderPage1() {
        const {
            onboarding,
            isSubmitting
        } = this.state;

        return (<>

            <Form.Group className="mb-4">
                <Form.Label>Please enter your full name</Form.Label>
                <div className="row">
                    <div className="col-md-6">
                        <input
                            className="form-control"
                            type="text"
                            placeholder="First name"
                            defaultValue={onboarding.instructor.firstName || ''}
                            onBlur={e => this.updateField('firstName', e.target.value)}
                        />
                    </div>
                    <div className="col-md-6">
                        <input
                            className="form-control"
                            type="text"
                            placeholder="Last name"
                            defaultValue={onboarding.instructor.lastName || ''}
                            onBlur={e => this.updateField('lastName', e.target.value)}
                        />
                    </div>
                </div>
            </Form.Group>

            <Form.Group className="mb-4">
                <Form.Label>
                    You must hold the AWS Solutions Architect certification as a prerequisite.<br />
                    Please enter the Credly link to verify your accreditation.
                </Form.Label>
                <input
                    type="text"
                    className="form-control"
                    placeholder="https://www.credly.com..."
                    defaultValue={onboarding.credlyURL || ''}
                    onBlur={e => this.updateField('credlyURL', e.target.value)}
                />
            </Form.Group>

            <Form.Group className="mb-4">
                <Form.Label>
                    Please include the Credly link(s) to other AWS certifications that you hold.
                </Form.Label>
                <textarea
                    className="form-control"
                    placeholder="https://www.credly.com..."
                    defaultValue={onboarding.otherCertLinks || ''}
                    onBlur={e => this.updateField('otherCertLinks', e.target.value)}
                />
            </Form.Group>

            <Form.Group>
                <Form.Label>
                    Are you an Amazon Authorised Instructor already?
                </Form.Label>
                <Form.Check
                    type="radio"
                    id="is-existing-aai-no"
                    checked={!onboarding.instructor.isExistingAAI}
                    onChange={e => this.updateField('isExistingAAI', false)}
                    label="No"
                />
                <Form.Check
                    type="radio"
                    id="is-existing-aai-yes"
                    checked={onboarding.instructor.isExistingAAI}
                    onChange={e => this.updateField('isExistingAAI', true)}
                    label="Yes"
                />
            </Form.Group>

        </>);
    }

    renderPage2() {
        const {
            onboarding,
            courses,
            isSubmitting
        } = this.state;

        return (<>

            {!onboarding.instructor.isExistingAAI &&
                <Form.Group className="mb-4">
                    <Form.Label>Tell us in your own words why you want to become an AAI.</Form.Label>
                    <textarea
                        className="form-control"
                        defaultValue={onboarding.whyWantToBecomeAAI || ''}
                        onBlur={e => this.updateField('whyWantToBecomeAAI', e.target.value)}
                    />
                </Form.Group>
            }

            {onboarding.instructor.isExistingAAI && <>
                {/*
                <Form.Group className="mb-4">
                    <Form.Label>
                        Please select the classes you are approved to deliver
                    </Form.Label>
                    <Select
                        className="react-select-container"
                        classNamePrefix="react-select"
                        isMulti
                        isClearable={false}
                        value={onboarding.instructor.courses}
                        options={courses}
                        getOptionValue={c => c.courseID || c.id}
                        getOptionLabel={c => c.courseName || c.name}
                        onChange={v => this.updateField('courses', v)}
                    />
                </Form.Group>
                */}

                <Form.Group className="mb-4">
                    <Form.Label>
                        Are you a standard or champion AAI?
                    </Form.Label>
                    <Form.Check
                        type="radio"
                        id="status-standard"
                        checked={onboarding.existingAAIStatus == 'Standard'}
                        onChange={e => this.updateField('existingAAIStatus', 'Standard')}
                        label="Standard"
                    />
                    <Form.Check
                        type="radio"
                        id="status-champion"
                        checked={onboarding.existingAAIStatus == 'Champion'}
                        onChange={e => this.updateField('existingAAIStatus', 'Champion')}
                        label="Champion"
                    />
                </Form.Group>

            </>}

            <Form.Group className="mb-4">
                <Form.Label>
                    Can we double check you have no contractual obligations which might stop you from being able to deliver in the freelancer programme?
                </Form.Label>
                <Form.Check
                    type="checkbox"
                    id="no-other-contractual-obligations"
                    checked={onboarding.noOtherContractualObligations || false}
                    onChange={e => this.updateField('noOtherContractualObligations', e.target.checked)}
                    label="I confirm"
                />
            </Form.Group>
            
            <Form.Group className="mb-4">
                <Form.Label>What is your current work situation, please select from the below:</Form.Label>
                <select
                    className="form-control"
                    value={onboarding.currentWorkSituation || ''}
                    onChange={e => this.updateField('currentWorkSituation', e.target.value)}
                >
                    <option value="">(Please select)</option>
                    <option value="FreelanceInstructor">I work as a freelance instructor</option>
                    <option value="SystemsArchitect">I work as a systems architect</option>
                    <option value="Consultant">I work as a consultant</option>
                    <option value="Other">Other, please tell us?</option>
                </select>
                {onboarding.currentWorkSituation == 'Other' &&
                    <textarea
                        defaultValue={onboarding.currentWorkSituationOther || ''}
                        onBlur={e => this.updateField('currentWorkSituationOther', e.target.value)}
                    />
                }
            </Form.Group>
            <Form.Group className="mb-4">
                <Form.Label>
                    You need to be able to offer us a guaranteed 15 days per year, but how many do you estimate you could give us?
                </Form.Label>
                <input
                    type="text"
                    className="form-control"
                    defaultValue={onboarding.canCommitDays || ''}
                    onBlur={e => this.updateField('canCommitDays', e.target.value)}
                />
            </Form.Group>

            <Form.Group>
                <Form.Label>
                    What hours can you work and in which time zone(s)?
                </Form.Label>
                <textarea
                    className="form-control"
                    defaultValue={onboarding.hours || ''}
                    onBlur={e => this.updateField('hours', e.target.value)}
                    placeholder="e.g. I can always work 9-5 CET, I cannot work any evenings or very early mornings"
                />
            </Form.Group>

        </>);
    }

    renderPage3() {
        const {
            onboarding,
            languages,
            countries,
            isLoadingCities,
            cities,
            isSubmitting
        } = this.state;

        return (<>

            <Form.Group className="mb-4">
                <Form.Label>
                    Which languages can you deliver in?
                </Form.Label>
                <Select
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isMulti
                    isClearable={false}
                    value={onboarding.instructor.languages}
                    options={languages}
                    getOptionValue={l => l.id}
                    getOptionLabel={l => l.name}
                    onChange={v => this.updateField('languages', v)}
                />
            </Form.Group>

            <Form.Group className="mb-4">
                <Form.Label>
                    Please enter your preferred email address
                </Form.Label>
                <input
                    className="form-control"
                    type="text"
                    defaultValue={onboarding.instructor.email || ''}
                    onBlur={e => this.updateField('email', e.target.value)}
                />
            </Form.Group>

            <Form.Group className="mb-4">
                <Form.Label>
                    Please enter your contact number
                </Form.Label>
                <input
                    type="text"
                    className="form-control"
                    defaultValue={onboarding.instructor.mobileNumber || ''}
                    onBlur={e => this.updateField('mobileNumber', e.target.value)}
                />
            </Form.Group>

            <Form.Group className="mb-4">
                <Form.Label>
                    Please select your country
                </Form.Label>
                <Select
                    className="react-select-container"
                    classNamePrefix="react-select"
                    placeholder="Country"
                    isClearable={false}
                    options={countries}
                    value={countries.find(c => c.id == onboarding.instructor.countryID)}
                    getOptionValue={c => c.id}
                    getOptionLabel={c => c.name}
                    onChange={country => this.updateField('countryID', country.id)}
                />
            </Form.Group>

            {!!onboarding.instructor.countryID &&
                <Form.Group className="mb-4">
                    <Form.Label>
                        Please select your city
                    </Form.Label>
                    {isLoadingCities ? <Loader isInline /> :
                        <Creatable
                            className="react-select-container mt-1"
                            classNamePrefix="react-select"
                            isClearable={false}
                            options={cities}
                            placeholder="City"
                            value={cities.find(c => c.id == onboarding.instructor.cityID)}
                            getOptionValue={c => c.id}
                            getOptionLabel={b => b.__isNew__ ? b.label : b.name}
                            formatCreateLabel={name => <>Add <b>{name}</b></>}
                            onChange={async city => {
                                if (city.__isNew__) {
                                    // Temp - add city for display
                                    let cities = [...this.state.cities];
                                    city.id = 'temp-new';
                                    this.setState({ cities }, () => {
                                        this.updateField('cityID', 'temp-new')
                                    });

                                    // Add city to DB and then select it properly
                                    cities = [...this.state.cities];
                                    city.countryID = onboarding.instructor.countryID;
                                    city.name = city.label;
                                    delete city.id;
                                    const response = await LocationService.addCity(city);
                                    city.id = response.id;
                                    cities.push(city);
                                    this.setState({ cities }, () => {
                                        this.updateField('cityID', city.id)
                                    });
                                } else {
                                    this.updateField('cityID', city.id)
                                }
                            }}
                        />
                    }
                </Form.Group>
            }
            <Form.Group className="mb-4">
                <Form.Label>
                    Please enter your full address including city
                </Form.Label>
                <textarea
                    className="form-control mt-1"
                    defaultValue={onboarding.instructor.address || ''}
                    onBlur={e => this.updateField('address', e.target.value)}
                />
            </Form.Group>

            <Form.Group>
                <Form.Label>
                    Thank you for your answers. Please add any other detail that you feel is relevant to the position.
                </Form.Label>
                <textarea
                    className="form-control"
                    defaultValue={onboarding.otherDetails || ''}
                    onBlur={e => this.updateField('otherDetails', e.target.value)}
                />
            </Form.Group>


        </>);
    }

    /*
    
        <Form.Group className="mb-4">
            <Form.Label>Were you recommended by an existing AAI? If yes, please specify who.</Form.Label>
            <input
                type="text"
                className="form-control"
                defaultValue={onboarding.recommendedByExistingAAI || ''}
                onBlur={e => this.updateField('recommendedByExistingAAI', e.target.value)}
            />
        </Form.Group>

        <Form.Group className="mb-4">
            <Form.Label>
                Which areas of AWS interest you most?
            </Form.Label>
            <textarea
                className="form-control"
                defaultValue={onboarding.areas || ''}
                onBlur={e => this.updateField('areas', e.target.value)}
            />
        </Form.Group>

    */
}

export default withRouter(OnboardingPage);